import * as THREE from "three";
import { gerstnerHeight } from "../ocean";
import { BIRD_PERCHES, DUCK_SITS, duckWaterSpots, type Spot } from "./places";

function std(color: number, roughness = 0.55): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color, roughness, metalness: 0.04 });
}

type BirdMode = "circle" | "approach" | "perch" | "takeoff" | "overfly" | "gone";

export class Seagull {
  readonly group: THREE.Group;
  private wingL: THREE.Object3D;
  private wingR: THREE.Object3D;
  private radius: number;
  private height: number;
  private speed: number;
  private phase: number;
  private cx: number;
  private cz: number;
  private mode: BirdMode;
  private timer = 0;
  private perchTime = 8;
  private perch: Spot | null = null;
  private from = new THREE.Vector3();

  constructor(seed: number, overfly = false) {
    this.group = makeGull();
    this.wingL = this.group.getObjectByName("wingL") as THREE.Object3D;
    this.wingR = this.group.getObjectByName("wingR") as THREE.Object3D;
    this.radius = 16 + (seed % 7) * 5;
    this.height = 8 + (seed % 5) * 2;
    this.speed = 0.32 + (seed % 4) * 0.07;
    this.phase = seed * 0.9;
    this.cx = ((seed * 13) % 28) - 14;
    this.cz = ((seed * 7) % 22) - 2;
    this.mode = overfly ? "overfly" : "circle";
    this.timer = seed * 1.7;
  }

  update(t: number, dt: number): void {
    this.timer += dt;
    if (this.mode === "overfly") {
      this.pass(t);
      return;
    }
    if (this.mode === "circle") {
      const a = t * this.speed + this.phase;
      this.group.position.set(
        this.cx + Math.cos(a) * this.radius,
        this.height + Math.sin(a * 2.2) * 0.7,
        this.cz + Math.sin(a) * this.radius * 0.65,
      );
      this.group.rotation.set(0, a + Math.PI / 2, Math.sin(a) * 0.1);
      this.flap(t, 11, 0.55);
      if (this.timer > 9 + (this.phase % 8)) {
        this.perch = BIRD_PERCHES[Math.floor((this.phase * 3 + t) % BIRD_PERCHES.length)];
        this.from.copy(this.group.position);
        this.mode = "approach";
        this.timer = 0;
      }
      return;
    }
    if (this.mode === "approach" && this.perch) {
      const u = Math.min(1, this.timer / 3.2);
      this.group.position.lerpVectors(this.from, new THREE.Vector3(this.perch.x, this.perch.y + 0.12, this.perch.z), u);
      this.group.rotation.x = THREE.MathUtils.lerp(0, 0.35, u);
      this.flap(t, 14, 0.4 * (1 - u));
      if (u >= 1) {
        this.mode = "perch";
        this.timer = 0;
        this.perchTime = 6 + (this.phase % 10);
        this.wingL.rotation.z = 0.08;
        this.wingR.rotation.z = -0.08;
      }
      return;
    }
    if (this.mode === "perch" && this.perch) {
      this.group.position.set(this.perch.x, this.perch.y + 0.1, this.perch.z);
      this.group.rotation.set(0.15, this.phase, 0);
      this.wingL.rotation.z = 0.06;
      this.wingR.rotation.z = -0.06;
      this.group.position.y += Math.sin(t * 1.4 + this.phase) * 0.02;
      if (this.timer > this.perchTime) {
        this.mode = "takeoff";
        this.timer = 0;
        this.from.copy(this.group.position);
      }
      return;
    }
    if (this.mode === "takeoff") {
      const u = Math.min(1, this.timer / 2.2);
      this.group.position.set(this.from.x + u * 4, this.from.y + u * 8, this.from.z + u * 2);
      this.flap(t, 16, 0.6);
      if (u >= 1) {
        this.mode = "circle";
        this.timer = 0;
      }
    }
  }

  private pass(t: number): void {
    const u = (t * 0.085 + this.phase * 0.1) % 1;
    const fly = u < 0.62;
    this.group.visible = fly;
    if (!fly) return;
    const p = u / 0.62;
    const x = THREE.MathUtils.lerp(-260, 260, p);
    const z = 10 + Math.sin(this.phase) * 50 + Math.sin(p * 3) * 18;
    const y = 16 + Math.sin(this.phase * 2) * 6;
    this.group.position.set(x, y, z);
    this.group.rotation.set(0, Math.PI / 2, 0);
    this.flap(t, 10, 0.45);
  }

  private flap(t: number, rate: number, amp: number): void {
    const f = Math.sin(t * rate + this.phase) * amp;
    this.wingL.rotation.z = 0.22 + f;
    this.wingR.rotation.z = -0.22 - f;
  }
}

function makeGull(): THREE.Group {
  const g = new THREE.Group();
  const body = new THREE.Mesh(new THREE.SphereGeometry(0.18, 7, 5), std(0xf4f7f8));
  body.scale.set(1.5, 0.7, 0.75);
  body.castShadow = true;
  g.add(body);
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.1, 6, 5), std(0xf4f7f8));
  head.position.set(0.2, 0.06, 0);
  g.add(head);
  const beak = new THREE.Mesh(new THREE.ConeGeometry(0.03, 0.12, 5), std(0xe85d4c, 0.4));
  beak.rotation.z = -Math.PI / 2;
  beak.position.set(0.3, 0.04, 0);
  g.add(beak);
  const wingGeo = new THREE.BoxGeometry(0.7, 0.03, 0.18);
  const wingL = new THREE.Mesh(wingGeo, std(0xf4f7f8));
  wingL.name = "wingL";
  wingL.position.set(0, 0.04, 0.28);
  g.add(wingL);
  const wingR = new THREE.Mesh(wingGeo, std(0xf4f7f8));
  wingR.name = "wingR";
  wingR.position.set(0, 0.04, -0.28);
  g.add(wingR);
  const tail = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.02, 0.12), std(0xe8ecec));
  tail.position.set(-0.26, 0.02, 0);
  g.add(tail);
  return g;
}

export class Tropicbird {
  readonly group: THREE.Group;
  private wingL: THREE.Object3D;
  private wingR: THREE.Object3D;
  private seed: number;

  constructor(seed: number) {
    this.seed = seed;
    this.group = makeTropic();
    this.wingL = this.group.getObjectByName("wingL") as THREE.Object3D;
    this.wingR = this.group.getObjectByName("wingR") as THREE.Object3D;
  }

  update(t: number): void {
    const u = (t * 0.05 + this.seed * 0.17) % 1;
    const fly = u < 0.55;
    this.group.visible = fly;
    if (!fly) return;
    const p = u / 0.55;
    this.group.position.set(
      THREE.MathUtils.lerp(-300, 300, p),
      32 + Math.sin(p * 4) * 4,
      40 + Math.sin(this.seed + p * 2) * 70,
    );
    this.group.rotation.y = Math.PI / 2;
    const flap = Math.sin(t * 4.5 + this.seed) * 0.22;
    this.wingL.rotation.z = 0.15 + flap;
    this.wingR.rotation.z = -0.15 - flap;
  }
}

function makeTropic(): THREE.Group {
  const g = makeGull();
  const streamer = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.015, 0.04), std(0xf4f7f8));
  streamer.position.set(-0.55, 0, 0);
  g.add(streamer);
  g.scale.setScalar(1.15);
  return g;
}

export class Frigate {
  readonly group: THREE.Group;
  private wingL: THREE.Object3D;
  private wingR: THREE.Object3D;
  private seed: number;

  constructor(seed: number) {
    this.seed = seed;
    this.group = makeFrigate();
    this.wingL = this.group.getObjectByName("wingL") as THREE.Object3D;
    this.wingR = this.group.getObjectByName("wingR") as THREE.Object3D;
  }

  update(t: number): void {
    const u = (t * 0.04 + this.seed * 0.31) % 1;
    const fly = u < 0.5;
    this.group.visible = fly;
    if (!fly) return;
    const p = u / 0.5;
    this.group.position.set(
      THREE.MathUtils.lerp(280, -280, p),
      38 + Math.cos(p * 3) * 3,
      20 + Math.cos(this.seed + p) * 80,
    );
    this.group.rotation.y = -Math.PI / 2;
    const flap = Math.sin(t * 3.2 + this.seed) * 0.16;
    this.wingL.rotation.z = 0.2 + flap;
    this.wingR.rotation.z = -0.2 - flap;
  }
}

function makeFrigate(): THREE.Group {
  const g = new THREE.Group();
  const dark = std(0x1b2429, 0.5);
  const body = new THREE.Mesh(new THREE.SphereGeometry(0.2, 7, 5), dark);
  body.scale.set(1.8, 0.55, 0.55);
  g.add(body);
  const wingGeo = new THREE.BoxGeometry(1.3, 0.03, 0.22);
  const wingL = new THREE.Mesh(wingGeo, dark);
  wingL.name = "wingL";
  wingL.position.set(0, 0.02, 0.45);
  g.add(wingL);
  const wingR = new THREE.Mesh(wingGeo, dark);
  wingR.name = "wingR";
  wingR.position.set(0, 0.02, -0.45);
  g.add(wingR);
  const fork = new THREE.Mesh(new THREE.BoxGeometry(0.35, 0.02, 0.22), dark);
  fork.position.set(-0.4, 0, 0);
  g.add(fork);
  g.scale.setScalar(1.3);
  return g;
}

export class Duck {
  readonly group: THREE.Group;
  readonly head: THREE.Object3D;
  offset: THREE.Vector3;
  private bob: number;

  constructor(seed: number, offset: THREE.Vector3) {
    this.group = makeDuck(seed % 3 !== 1, seed % 7 === 0);
    this.head = this.group.userData.head as THREE.Object3D;
    this.offset = offset;
    this.bob = seed;
  }

  place(x: number, y: number, z: number, heading: number, t: number, dabble: boolean, sitting: boolean): void {
    this.group.position.set(x, y, z);
    this.group.rotation.y = heading;
    this.group.rotation.z = sitting ? 0 : Math.sin(t * 2.2 + this.bob) * 0.06;
    this.head.rotation.x = dabble ? 0.9 + Math.sin(t * 3 + this.bob) * 0.15 : Math.sin(t * 1.2 + this.bob) * 0.08;
  }
}

function makeDuck(male: boolean, duckling: boolean): THREE.Group {
  const g = new THREE.Group();
  const bodyC = duckling ? 0xd4a017 : male ? 0x4a5a32 : 0x8a6a3a;
  const body = new THREE.Mesh(new THREE.SphereGeometry(0.16, 7, 5), std(bodyC));
  body.scale.set(1.4, 0.85, 1);
  g.add(body);
  const head = new THREE.Group();
  head.position.set(0.16, 0.1, 0);
  const skull = new THREE.Mesh(
    new THREE.SphereGeometry(0.1, 6, 5),
    std(duckling ? 0xd4a017 : male ? 0x1f7a4d : 0x8a6a3a),
  );
  head.add(skull);
  const bill = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.035, 0.05), std(0xe8a030));
  bill.position.set(0.1, -0.02, 0);
  head.add(bill);
  g.add(head);
  g.userData.head = head;
  if (duckling) g.scale.setScalar(0.55);
  return g;
}

export class DuckFlock {
  readonly ducks: Duck[] = [];
  readonly group = new THREE.Group();
  private target = new THREE.Vector3();
  private heading = 0;
  private state: "swim" | "dabble" | "haul" = "swim";
  private timer = 0;
  private sit: Spot | null = null;
  private spots: Array<[number, number]>;
  private seed: number;

  constructor(count: number, seed: number, spots: Array<[number, number]>) {
    this.seed = seed;
    this.spots = spots;
    const [sx, sz] = spots[seed % spots.length];
    this.target.set(sx, 0, sz);
    for (let i = 0; i < count; i++) {
      const duck = new Duck(seed + i * 3, new THREE.Vector3((i - (count - 1) / 2) * 0.85, 0, (i % 2) * -0.55));
      this.ducks.push(duck);
      this.group.add(duck.group);
    }
  }

  update(t: number, dt: number, others: DuckFlock[]): void {
    this.timer += dt;
    if (this.state === "swim" && this.timer > 4 && others.length) {
      const close = others.find((o) => o !== this && o.state === "swim" && o.target.distanceTo(this.target) < 11);
      if (close && Math.sin(t * 0.2 + this.seed) > 0.4) {
        this.target.copy(close.target);
        this.timer = 0;
      }
    }
    if (this.state === "swim" && this.timer > 7 + (this.seed % 5)) {
      this.state = Math.random() > 0.55 ? "dabble" : "haul";
      this.timer = 0;
      if (this.state === "haul") this.sit = DUCK_SITS[(this.seed + Math.floor(t)) % DUCK_SITS.length];
      else this.pickWater();
    }
    if (this.state === "dabble" && this.timer > 5) {
      this.state = "swim";
      this.timer = 0;
      this.pickWater();
    }
    if (this.state === "haul" && this.timer > 9) {
      this.state = "swim";
      this.timer = 0;
      this.sit = null;
      this.pickWater();
    }

    if (this.state !== "haul") {
      const wander = t * 0.25 + this.seed;
      this.target.x += Math.cos(wander) * dt * 0.35;
      this.target.z += Math.sin(wander * 1.1) * dt * 0.35;
    }

    const goal = this.sit
      ? new THREE.Vector3(this.sit.x, this.sit.y, this.sit.z)
      : this.target;
    this.heading = Math.atan2(goal.x - this.target.x + 0.01, goal.z - this.target.z);
    const dabble = this.state === "dabble";
    const sitting = this.state === "haul" && this.timer > 1.6;

    for (const duck of this.ducks) {
      const ox = duck.offset.x;
      const oz = duck.offset.z;
      const x = THREE.MathUtils.damp(duck.group.position.x, goal.x + ox, 1.8, dt);
      const z = THREE.MathUtils.damp(duck.group.position.z, goal.z + oz, 1.8, dt);
      const y = sitting && this.sit ? this.sit.y : gerstnerHeight(x, z, t) + 0.07;
      duck.place(x, y, z, this.heading, t, dabble && !sitting, sitting);
    }
  }

  private pickWater(): void {
    const [x, z] = this.spots[(this.seed + Math.floor(this.timer * 3)) % this.spots.length];
    this.target.set(x, 0, z);
  }
}

export class Heron {
  readonly group: THREE.Group;
  private neck: THREE.Object3D;
  private x: number;
  private z: number;

  constructor(x: number, z: number) {
    this.x = x;
    this.z = z;
    this.group = makeHeron();
    this.neck = this.group.getObjectByName("neck") as THREE.Object3D;
    this.group.position.set(x, 0.05, z);
  }

  update(t: number): void {
    const y = gerstnerHeight(this.x, this.z, t) + 0.04;
    this.group.position.y = y;
    this.neck.rotation.x = -0.4 + Math.sin(t * 0.7) * 0.15;
    this.group.rotation.y = Math.sin(t * 0.25) * 0.4;
  }
}

function makeHeron(): THREE.Group {
  const g = new THREE.Group();
  const grey = std(0x9aa3a8, 0.6);
  const body = new THREE.Mesh(new THREE.SphereGeometry(0.16, 7, 5), grey);
  body.scale.set(0.8, 1.1, 1.6);
  body.position.y = 0.55;
  g.add(body);
  const neck = new THREE.Group();
  neck.name = "neck";
  neck.position.set(0.12, 0.75, 0);
  const n = new THREE.Mesh(new THREE.CapsuleGeometry(0.035, 0.45, 2, 5), grey);
  n.position.y = 0.25;
  neck.add(n);
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.07, 6, 5), std(0xe8ecec));
  head.position.set(0.08, 0.5, 0);
  neck.add(head);
  const beak = new THREE.Mesh(new THREE.ConeGeometry(0.02, 0.18, 5), std(0xf2c14e));
  beak.rotation.z = -Math.PI / 2;
  beak.position.set(0.18, 0.48, 0);
  neck.add(beak);
  g.add(neck);
  const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.015, 0.02, 0.55, 4), std(0xe8a030));
  const l1 = leg.clone();
  l1.position.set(-0.05, 0.28, 0.04);
  const l2 = leg.clone();
  l2.position.set(0.05, 0.28, -0.04);
  g.add(l1, l2);
  return g;
}

export class Dolphin {
  readonly group: THREE.Group;
  private seed: number;
  private laneX: number;
  private laneZ: number;

  constructor(seed: number) {
    this.seed = seed;
    this.laneX = -20 + (seed % 5) * 12;
    this.laneZ = 95 + (seed % 4) * 18;
    this.group = makeDolphin();
  }

  update(t: number): void {
    const cycle = 7.5;
    const local = (t * 0.55 + this.seed * 1.3) % cycle;
    const swimZ = this.laneZ + ((t * 1.6 + this.seed * 10) % 90) - 20;
    const x = this.laneX + Math.sin(t * 0.4 + this.seed) * 4;
    if (local < 1.8) {
      const u = local / 1.8;
      const y = 4 * u * (1 - u) * 3.8;
      this.group.position.set(x, y, swimZ);
      this.group.rotation.x = THREE.MathUtils.lerp(0.5, -0.7, u);
      this.group.rotation.z = Math.sin(u * Math.PI) * 0.25;
    } else {
      const y = gerstnerHeight(x, swimZ, t) - 0.35 + Math.sin(t * 2 + this.seed) * 0.1;
      this.group.position.set(x, y, swimZ);
      this.group.rotation.x = 0.05;
      this.group.rotation.z = Math.sin(t * 2 + this.seed) * 0.08;
    }
    this.group.rotation.y = Math.PI / 2;
  }
}

function makeDolphin(): THREE.Group {
  const g = new THREE.Group();
  const grey = std(0x7a8b96, 0.4);
  const body = new THREE.Mesh(new THREE.SphereGeometry(0.45, 8, 6), grey);
  body.scale.set(2.2, 0.7, 0.7);
  g.add(body);
  const snout = new THREE.Mesh(new THREE.CapsuleGeometry(0.12, 0.35, 3, 6), grey);
  snout.rotation.z = Math.PI / 2;
  snout.position.set(0.85, 0.02, 0);
  g.add(snout);
  const fin = new THREE.Mesh(new THREE.ConeGeometry(0.16, 0.35, 5), grey);
  fin.position.set(0.05, 0.38, 0);
  g.add(fin);
  const tail = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.04, 0.55), grey);
  tail.position.set(-0.95, 0.02, 0);
  g.add(tail);
  g.scale.setScalar(1.15);
  return g;
}

export class Seal {
  readonly group: THREE.Group;
  private hauled: boolean;
  private x: number;
  private z: number;
  private phase: number;
  private swimR: number;

  constructor(x: number, z: number, hauled: boolean, seed: number) {
    this.x = x;
    this.z = z;
    this.hauled = hauled;
    this.phase = seed;
    this.swimR = 4 + (seed % 3);
    this.group = makeSeal();
    if (hauled) {
      this.group.position.set(x, 0.25, z);
      this.group.rotation.y = seed * 0.7;
      this.group.rotation.z = 0.15;
    }
  }

  update(t: number): void {
    if (this.hauled) {
      this.group.rotation.x = Math.sin(t * 0.8 + this.phase) * 0.06;
      this.group.position.y = 0.22 + Math.sin(t * 0.5 + this.phase) * 0.02;
      return;
    }
    const a = t * 0.35 + this.phase;
    const x = this.x + Math.cos(a) * this.swimR;
    const z = this.z + Math.sin(a) * this.swimR;
    const y = gerstnerHeight(x, z, t) + 0.05;
    this.group.position.set(x, y, z);
    this.group.rotation.y = a + Math.PI / 2;
    this.group.rotation.x = Math.sin(t * 2.2 + this.phase) * 0.12;
  }
}

function makeSeal(): THREE.Group {
  const g = new THREE.Group();
  const brown = std(0x5a4638, 0.7);
  const body = new THREE.Mesh(new THREE.SphereGeometry(0.38, 8, 6), brown);
  body.scale.set(1.8, 0.7, 0.75);
  g.add(body);
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.2, 7, 5), brown);
  head.position.set(0.55, 0.12, 0);
  g.add(head);
  const flip = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.05, 0.18), brown);
  const f1 = flip.clone();
  f1.position.set(0.15, -0.12, 0.28);
  f1.rotation.z = 0.4;
  const f2 = flip.clone();
  f2.position.set(0.15, -0.12, -0.28);
  f2.rotation.z = -0.4;
  g.add(f1, f2);
  return g;
}

export function spawnWildlife(): {
  seagulls: Seagull[];
  tropicbirds: Tropicbird[];
  frigates: Frigate[];
  flocks: DuckFlock[];
  herons: Heron[];
  dolphins: Dolphin[];
  seals: Seal[];
} {
  const seagulls = [
    ...Array.from({ length: 7 }, (_, i) => new Seagull(i + 1, false)),
    ...Array.from({ length: 5 }, (_, i) => new Seagull(i + 20, true)),
  ];
  const tropicbirds = Array.from({ length: 3 }, (_, i) => new Tropicbird(i + 2));
  const frigates = Array.from({ length: 2 }, (_, i) => new Frigate(i + 1));
  const spots = duckWaterSpots();
  const flocks = [
    new DuckFlock(2, 1, spots),
    new DuckFlock(3, 4, spots),
    new DuckFlock(2, 8, spots),
    new DuckFlock(3, 11, spots),
  ];
  const herons = [new Heron(-36, 4), new Heron(32, -10)];
  const dolphins = Array.from({ length: 5 }, (_, i) => new Dolphin(i + 1));
  const seals = [
    new Seal(-6, 70, true, 1),
    new Seal(-2, 68, true, 2),
    new Seal(30, 69, true, 3),
    new Seal(34, 66, true, 4),
    new Seal(-8, 78, false, 5),
    new Seal(28, 80, false, 6),
  ];
  return { seagulls, tropicbirds, frigates, flocks, herons, dolphins, seals };
}
